import { initializeApp } from 'firebase/app';
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { FIREBASE_CONFIG } from '../config/config';
import logger from './logger';

// Initialiser Firebase
let app;
let auth;

try {
  app = initializeApp(FIREBASE_CONFIG);
  auth = getAuth(app);

  // Initialiser la persistence de manière asynchrone
  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      logger.log('✅ Firebase persistence initialisée');
    })
    .catch((error) => {
      logger.warn('⚠️ Erreur lors de la configuration de la persistence Firebase:', error);
      // Continuer même si la persistence échoue (peut arriver en iframe)
    });
} catch (error) {
  logger.error('Erreur lors de l\'initialisation de Firebase:', error);
}

/**
 * Configure le reCAPTCHA invisible pour la vérification du téléphone
 * @param {string} containerId - ID du conteneur pour le reCAPTCHA
 * @param {boolean} invisible - Mode invisible (par défaut: true)
 * @returns {RecaptchaVerifier}
 */
export const setupRecaptcha = (containerId = 'recaptcha-container', invisible = true) => {
  if (!auth) {
    throw new Error('Firebase Auth n\'est pas initialisé');
  }

  try {
    const recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: invisible ? 'invisible' : 'normal',
      callback: (response) => {
        logger.log('reCAPTCHA résolu', response);
      },
      'expired-callback': () => {
        logger.log('reCAPTCHA expiré, rechargez la page');
      },
    });

    return recaptchaVerifier;
  } catch (error) {
    logger.error('Erreur lors de la configuration du reCAPTCHA:', error);
    throw error;
  }
};

/**
 * Envoie un code de vérification au numéro de téléphone
 * @param {string} phoneNumber - Numéro de téléphone au format international (ex: +33612345678)
 * @param {RecaptchaVerifier} recaptchaVerifier - Instance du reCAPTCHA
 * @returns {Promise<ConfirmationResult>}
 */
export const sendVerificationCode = async (phoneNumber, recaptchaVerifier) => {
  if (!auth) {
    throw new Error('Firebase Auth n\'est pas initialisé');
  }

  try {
    const confirmationResult = await signInWithPhoneNumber(
      auth,
      phoneNumber,
      recaptchaVerifier
    );
    return confirmationResult;
  } catch (error) {
    logger.error('Erreur lors de l\'envoi du code:', error);
    throw error;
  }
};

/**
 * Vérifie le code de vérification entré par l'utilisateur
 * @param {ConfirmationResult} confirmationResult - Résultat de la confirmation
 * @param {string} code - Code de vérification à 6 chiffres
 * @returns {Promise<UserCredential>}
 */
export const verifyCode = async (confirmationResult, code) => {
  try {
    const result = await confirmationResult.confirm(code);
    return result;
  } catch (error) {
    logger.error('Erreur lors de la vérification du code:', error);
    throw error;
  }
};

/**
 * Récupère l'utilisateur actuellement connecté
 * @returns {User|null}
 */
export const getCurrentUser = () => {
  return auth?.currentUser || null;
};

/**
 * Nettoie les données Firebase stockées dans IndexedDB
 * @returns {Promise<void>}
 */
const clearFirebaseIndexedDB = async () => {
  try {
    // Liste des bases de données Firebase à supprimer
    const firebaseDBNames = [
      'firebaseLocalStorageDb',
      'firebase-heartbeat-database',
      'firebase-installations-database',
    ];

    // Supprimer chaque base de données
    for (const dbName of firebaseDBNames) {
      try {
        await new Promise((resolve) => {
          const request = indexedDB.deleteDatabase(dbName);
          request.onsuccess = () => {
            logger.log(`✅ IndexedDB "${dbName}" supprimée`);
            resolve();
          };
          request.onerror = () => {
            logger.warn(`⚠️ Erreur lors de la suppression de "${dbName}"`);
            resolve(); // On continue même en cas d'erreur
          };
          request.onblocked = () => {
            logger.warn(`⚠️ Suppression de "${dbName}" bloquée`);
            resolve(); // On continue même si bloqué
          };
        });
      } catch (e) {
        logger.warn(`⚠️ Impossible de supprimer "${dbName}":`, e);
      }
    }

    logger.log('✅ Nettoyage IndexedDB Firebase terminé');
  } catch (error) {
    logger.warn('⚠️ Erreur lors du nettoyage IndexedDB:', error);
  }
};

/**
 * Déconnecte l'utilisateur et nettoie les données en cache
 * @returns {Promise<void>}
 */
export const signOut = async () => {
  if (!auth) {
    throw new Error('Firebase Auth n\'est pas initialisé');
  }

  try {
    await auth.signOut();

    // Nettoyer les données Firebase dans IndexedDB
    await clearFirebaseIndexedDB();

    logger.log('✅ Déconnexion complète effectuée');
  } catch (error) {
    logger.error('Erreur lors de la déconnexion:', error);
    throw error;
  }
};

/**
 * Écoute les changements d'état de l'authentification
 * @param {function} callback - Fonction appelée lors des changements d'état
 * @returns {function} Fonction pour se désabonner
 */
export const onAuthStateChanged = (callback) => {
  if (!auth) {
    throw new Error('Firebase Auth n\'est pas initialisé');
  }

  return auth.onAuthStateChanged(callback);
};

export { auth };
